import type { AppRouterOutputs } from "@editor/utils/trpc";
import type { MenuItem } from "@replo/design-system/components/menu/Menu";
import type { Column as TableColumn } from "@replo/design-system/components/table/Table";
import type { WorkspaceRole } from "schemas/generated/workspace";

import * as React from "react";

import Avatar from "@common/designSystem/Avatar";
import Input from "@editor/components/common/designSystem/Input";
import Header from "@editor/components/dashboard/Header";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { getProjectName } from "@editor/utils/project-utils";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu } from "@replo/design-system/components/menu/Menu";
import Popover from "@replo/design-system/components/popover/Popover";
import Table from "@replo/design-system/components/table/Table";
import { skipToken as reactQuerySkipToken } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowDown10,
  ArrowDownAZ,
  ArrowUp,
  ArrowUp01,
  ArrowUpZA,
  ChevronDown,
  MoreHorizontal,
  Plus,
  Search,
} from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

type ProjectMember =
  AppRouterOutputs["workspace"]["getWorkspaceAndProjectMembers"][0];

type Field = "member.username" | "member.role" | "member.registeredAt";
type Direction = "asc" | "desc";

type OrderState = {
  label: string;
  icon: React.ReactNode;
  field: Field;
  direction: Direction;
};

const sortOptions: OrderState[] = [
  {
    label: "Last added",
    field: "member.registeredAt",
    direction: "desc",
    icon: <ArrowDown10 size={14} />,
  },
  {
    label: "First added",
    field: "member.registeredAt",
    direction: "asc",
    icon: <ArrowUp01 size={14} />,
  },
  {
    label: "Name A-Z",
    field: "member.username",
    direction: "asc",
    icon: <ArrowDownAZ size={14} />,
  },
  {
    label: "Name Z-A",
    field: "member.username",
    direction: "desc",
    icon: <ArrowUpZA size={14} />,
  },
  {
    label: "Most Permissions",
    field: "member.role",
    direction: "desc",
    icon: <ArrowDown size={14} />,
  },
  {
    label: "Least Permissions",
    field: "member.role",
    direction: "asc",
    icon: <ArrowUp size={14} />,
  },
];

const Members: React.FC = () => {
  const { user: currentUser } = useCurrentUser();

  const { openModal } = useModal();
  const [searchQueryInputValue, searchQuery, setSearchQueryInputValue] =
    useDebouncedState("");
  const [order, setOrder] = React.useState<OrderState>({
    label: "Most Permissions",
    field: "member.role",
    direction: "desc",
    icon: <ArrowDown size={14} />,
  });

  const { workspace, isLoading: isLoadingWorkspace } =
    useCurrentWorkspaceContext();
  const workspaceId = workspace?.id;

  const { data: members, isPending: isLoadingMembers } =
    trpc.workspace.getWorkspaceAndProjectMembers.useQuery(
      workspaceId ?? reactQuerySkipToken,
    );
  const { data: projects } = trpc.project.listWithStats.useQuery({});

  const logEvent = useLogAnalytics();
  const currentUserIsOwner = useIsWorkspaceOwner(workspaceId);

  const { mutate: resendInvite } = trpc.workspace.resendInvite.useMutation({
    onSuccess: (_, variables) => {
      successToast("Invites sent", "The invites were sent successfully.");
      logEvent("workspace.invite.resend", {
        resendInviteTo: variables?.email ?? "",
        workspaceId: variables?.workspaceId ?? "",
      });
    },
  });

  const { mutate: updatePermissions } =
    trpc.workspace.updatePermission.useMutation({
      onSuccess: () => {
        if (workspaceId) {
          void trpcUtils.workspace.getWorkspaceAndProjectMembers.invalidate(
            workspaceId,
          );
        }
      },
    });

  const handleUpdatePermissions = async (
    workspaceId: string,
    email: string,
    currentRole: WorkspaceRole,
  ) => {
    const isUserMember = currentRole === "member";
    const newRole = isUserMember ? "owner" : "member";
    void updatePermissions({
      email,
      workspaceId,
      role: newRole,
    });
  };

  const filteredMembers = members?.filter((member) => {
    const query = searchQuery.toLowerCase();
    const fullName = [member.firstName, member.lastName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const email = member.email.toLowerCase();

    return fullName.includes(query) || email.includes(query);
  });

  const sortedProjectMembers = React.useMemo(
    () =>
      filteredMembers &&
      sortMembers(order.field, order.direction, filteredMembers).map(
        (member) => ({ ...member, key: member.id }),
      ),
    [filteredMembers, order.field, order.direction],
  );

  const numRegisteredOwners =
    members?.filter(
      (member) =>
        member.workspaceMemberships.length > 0 &&
        member.workspaceMemberships[0]?.role === "owner" &&
        member.registeredAt,
    ).length ?? 0;

  const memberColumns: TableColumn<ProjectMember & { key: string }>[] = [
    {
      key: "name",
      header: "Name",
      renderCell: (member) => (
        <div className="flex items-center gap-2">
          <Avatar
            name={member.firstName}
            className="h-8 w-8"
            imageClassName="bg-info-soft text-primary header-h5"
          />
          <div className="flex flex-col overflow-hidden">
            {member.firstName ? (
              <p className="text-default truncate text-base">
                {member.firstName} {member.lastName}
              </p>
            ) : (
              <p className="text-placeholder truncate text-base">Invited</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      renderCell: (member) => (
        <p className="text-default truncate">{member.email}</p>
      ),
    },
    {
      key: "role",
      header: "Role",
      renderCell: (member) => {
        const role = member.workspaceMemberships[0]?.role;
        return role ? (
          <p className="text-default capitalize">{role}</p>
        ) : (
          <div>
            <Popover.Root>
              <Popover.Trigger>
                <div className="text-primary">
                  Can edit {member.projectMemberships.length} shop
                  {member.projectMemberships.length === 1 ? "" : "s"}
                </div>
              </Popover.Trigger>
              <Popover.Content
                shouldPreventDefaultOnInteractOutside={false}
                className="max-w-[300px] p-4 gap-2"
                title="Shops"
              >
                <div className="max-h-[180px] overflow-scroll text-sm divide-y divide-secondary flex flex-col gap-2">
                  {member.projectMemberships.map(({ project }) => {
                    const fullProject = projects?.projects.find(
                      (p) => p.id === project.id,
                    );
                    return (
                      <div
                        key={project.id}
                        className="text-muted truncate first:pt-0 last:pb-0"
                      >
                        {getProjectName(fullProject)}
                      </div>
                    );
                  })}
                </div>
              </Popover.Content>
            </Popover.Root>
            <div className="text-muted text-sm">
              No workspace level access or permissions
            </div>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: null,
      align: "right",
      renderCell: (member) => {
        const menuItems = getMemberMenuItems(member);
        return menuItems.length > 0 ? (
          <Menu
            items={menuItems}
            align="end"
            trigger={
              <IconButton
                variant="tertiary"
                size="sm"
                icon={<MoreHorizontal size={16} className="text-muted" />}
              />
            }
          />
        ) : null;
      },
    },
  ];

  const getMemberMenuItems = (member: ProjectMember): MenuItem[] => {
    if (!currentUser || !workspaceId) {
      return [];
    }

    const menuItems: MenuItem[] = [];
    const role = member.workspaceMemberships[0]?.role as
      | WorkspaceRole
      | undefined;
    const isOnlyRegisteredOwner =
      currentUserIsOwner &&
      numRegisteredOwners === 1 &&
      currentUser.email === member.email;

    if (!isOnlyRegisteredOwner) {
      menuItems.push({
        id: "remove-membership",
        type: "leaf",
        variant: "default",
        size: "sm",
        title: `Remove ${member.firstName ?? member.email}`,
        onSelect: () => {
          if (workspaceId) {
            openModal({
              type: "removeMemberModal",
              props: { workspaceId, user: member },
            });
          }
        },
      });
    }

    if (!member.registeredAt) {
      menuItems.push({
        id: "resend-email",
        type: "leaf",
        variant: "default",
        size: "sm",
        title: `Resend invitation to ${member.email}`,
        onSelect: () =>
          void resendInvite({
            email: member.email,
            workspaceId,
          }),
      });
    }

    if (currentUserIsOwner && currentUser.id !== member.id && role) {
      const isUserMember = role === "member";
      menuItems.push({
        id: "update-permissions",
        type: "leaf",
        variant: "default",
        size: "sm",
        title: isUserMember ? "Upgrade to Owner" : "Downgrade to Member",
        onSelect: () => {
          if (workspaceId) {
            void handleUpdatePermissions(workspaceId, member.email, role);
          }
        },
      });
    }

    if (currentUserIsOwner && currentUser.id !== member.id && !role) {
      menuItems.push({
        id: "invite-member",
        type: "leaf",
        variant: "default",
        size: "sm",
        title: "Invite to Workspace",
        onSelect: () => {
          if (workspaceId) {
            openModal({
              type: "inviteTeamMembersModal",
              props: { workspaceId, email: member.email },
            });
          }
        },
      });
    }
    return menuItems;
  };

  return (
    <div className="flex w-full flex-col gap-4 px-6 pb-14">
      <Header
        title="Workspace Members"
        size="sm"
        endEnhancer={
          <Button
            variant="primary"
            size="base"
            startEnhancer={<Plus size={16} />}
            onClick={() => {
              if (workspaceId) {
                openModal({
                  type: "inviteTeamMembersModal",
                  props: { workspaceId },
                });
              }
            }}
          >
            Add Members
          </Button>
        }
        isLoading={isLoadingMembers ?? isLoadingWorkspace}
      />
      <div className="flex items-center gap-2 mb-4 flex-shrink-0">
        <Input
          size="base"
          placeholder="Search by name or email"
          type="text"
          value={searchQueryInputValue}
          onChange={(event) => setSearchQueryInputValue(event.target.value)}
          startEnhancer={<Search size={14} className="text-muted" />}
        />
        <Menu
          items={sortOptions.map((option, index) => ({
            id: index.toString(),
            ...option,
            type: "leaf",
            variant: "default",
            size: "base",
            title: option.label,
            startEnhancer: option.icon,
            onSelect: () => {
              setOrder(option);
            },
          }))}
          align="end"
          trigger={
            <Button
              variant="secondary"
              size="base"
              endEnhancer={<ChevronDown size={14} />}
              startEnhancer={
                React.isValidElement(order.icon) ? order.icon : undefined
              }
            >
              {order.label}
            </Button>
          }
        />
      </div>
      <Table
        data={sortedProjectMembers ?? []}
        columns={memberColumns}
        isLoading={isLoadingMembers}
      />
    </div>
  );
};

const sortMembers = (
  field: Field,
  direction: Direction,
  data: ProjectMember[],
) => {
  const sortedData = [...(data ?? [])];
  sortedData.sort((a, b) => {
    if (field === "member.username" && a.firstName && b.firstName) {
      return direction === "asc"
        ? a.firstName.localeCompare(b.firstName)
        : b.firstName.localeCompare(a.firstName);
    }

    if (field === "member.role") {
      const aRole = (a.workspaceMemberships[0]?.role ?? "none") as
        | WorkspaceRole
        | "none";
      const bRole = (b.workspaceMemberships[0]?.role ?? "none") as
        | WorkspaceRole
        | "none";
      const rolesOrder: Record<
        WorkspaceRole | "none",
        { asc: number; desc: number }
      > = {
        owner: { asc: 1, desc: -1 },
        member: { asc: 0, desc: 0 },
        viewer: { asc: -1, desc: 1 },
        none: { asc: -2, desc: 2 },
      };
      return rolesOrder[aRole][direction] - rolesOrder[bRole][direction];
    }

    if (field === "member.registeredAt" && a.registeredAt && b.registeredAt) {
      return direction === "asc"
        ? a.registeredAt.getTime() - b.registeredAt.getTime()
        : b.registeredAt.getTime() - a.registeredAt.getTime();
    }

    return 0;
  });
  return sortedData;
};

export default Members;
